// [S-060a] Application sidebar — Workspace + Operations + Tenant admin
// groups. Mirrors the wireframe set (`docs/Wireframes/cs-components.js`
// NAV) so the visual diff is direct. Entries whose screens haven't
// shipped yet route to the NotYetBuilt empty-state with the story id
// that's owed, so the navigation graph is complete and honest.
//
// Group visibility:
//   - Workspace: visible to all roles
//   - Operations: visible to all roles
//   - Tenant admin: visible to tenant_admin OR platform_admin only
//
// "Attestation" is the wireframe's single sidebar entry that fans out
// to per-template evidence chain (S-092) for most roles and to the
// multi-template re-attestation schedule (S-063) for platform_admin.
// We compute the destination at session resolution time so the link
// honors the user's role without a redirect.
import { useState } from "react";
import { Link, NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useSession } from "../auth/AuthProvider";
import { sessionCan } from "../auth/capabilities";
import type { WebSession } from "../auth/types";
import { useChatSessions, useDeleteChatSession } from "../data/chatQuery";
import { UserMenu } from "./UserMenu";

type NavItem = {
  to: string;
  label: string;
  icon: () => JSX.Element;
  count?: number;
};

function workspaceFor(session: WebSession | null): NavItem[] {
  // Attestation routes fan out by capability. A principal who manages the
  // re-attestation SCHEDULE (reattestation:manage — platform reach) lands on
  // the schedule view; everyone else on the per-template browser.
  const attestationTo = sessionCan(session, "reattestation:manage", {
    layer: "L1",
  })
    ? "/reattestation"
    : "/attestation";
  const items: NavItem[] = [
    { to: "/home", label: "Home", icon: HomeIcon },
    { to: "/library", label: "Templates", icon: CertifiedIcon },
  ];
  // Precedents + Draft Workspace are per-studio — the backend rejects a caller
  // with no tenant (studioId null). Gate on tenant presence, not a role name.
  if (session?.studioId != null) {
    items.push({ to: "/precedent", label: "Precedent", icon: SearchIcon });
    items.push({ to: "/drafts", label: "Draft Workspace", icon: DraftIcon });
  }
  items.push(
    { to: attestationTo, label: "Approvals", icon: ShieldIcon },
    { to: "/briefs", label: "Pre-negotiation briefs", icon: BriefIcon },
  );
  return items;
}

// Operations group — only routes that actually render content. The three
// stubs that were here previously (Analytics → S-091, Ingestion → S-093,
// Review queue → S-093) led to the "Not yet built" placeholder for every
// role, which read as broken navigation to users. Routes are still
// registered in App.tsx so deep-linked URLs surface the explanation
// instead of 404, but the sidebar entries are hidden until the screens
// ship. Re-add the corresponding NavItem here once the underlying story
// merges.
const OPERATIONS: NavItem[] = [
  { to: "/activity", label: "Activity", icon: ClockIcon },
  { to: "/index-health", label: "Search status", icon: PulseIcon },
  { to: "/upload", label: "Upload portal", icon: UploadIcon },
];

// Tenant admin items are session-dependent — "Users" deep-links to the
// caller's own tenant for tenant_admin. platform_admin sees the same
// group label but their Users surface lives behind the Platform-section
// "Tenants" entry (they pick an organization first, then drill in). Same logic
// for "Template management": tenant_admin authors / manages L2 templates
// for their own tenant from here; platform_admin reaches the same page
// from the PLATFORM-section entry below (the page itself filters by the
// caller's authorable layer).
// The tenant "Organization" group — each entry is gated by the CAPABILITY that
// its screen requires, so a custom persona sees exactly the admin surfaces it
// can use (e.g. a template_manager sees Template Management but not Users),
// instead of the whole group being all-or-nothing on the tenant_admin role.
// Every entry is tenant-scoped, so they all require an own studio.
function tenantAdminFor(session: WebSession | null): NavItem[] {
  const items: NavItem[] = [];
  const studioId = session?.studioId;
  if (studioId !== null && studioId !== undefined) {
    if (sessionCan(session, "tenant:manage")) {
      items.push({
        to: `/admin/tenants/tenant-${studioId}/users`,
        label: "Users",
        icon: UsersIcon,
      });
    }
    if (sessionCan(session, "template:author")) {
      // [S-721] Sits before Template Management: browse/import/exclude/rate
      // is the "decide what belongs in my library" step; Template
      // Management is where an imported (or custom) L2 template gets
      // edited and certified. Same capability gate as the route itself.
      items.push({
        to: "/marketplace",
        label: "Marketplace",
        icon: MarketplaceIcon,
      });
      items.push({
        to: "/template-management",
        label: "Template Management",
        icon: StackIcon,
      });
    }
    // S-320c — tenant classification (fork-from-platform). Distinct from the
    // platform "Classification" in the PLATFORM group.
    if (sessionCan(session, "classification:manage")) {
      items.push({
        to: "/settings/classification",
        label: "Classification",
        icon: TagIcon,
      });
    }
    // S-411 — reviews/approves the tenant's own ingest queue (route + backend
    // scope to the caller's tenant).
    if (sessionCan(session, "review:moderate")) {
      items.push({ to: "/queue", label: "Review queue", icon: ShieldIcon });
    }
  }
  // Settings is the tenant-config console — gate on tenant:manage (its route
  // requires it). platform_admin holds it and reaches Settings here too.
  if (sessionCan(session, "tenant:manage")) {
    items.push({ to: "/settings", label: "Settings", icon: CogIcon });
  }
  // Platform atlas — opened to tenant admins, who get the executive-summary
  // deck only (PlatformAtlas gates the Overview/Roadmap inventories on
  // platform reach). Same tenant:manage bar as Settings. platform_admin
  // reaches it from the PLATFORM group below, so skip it here for them
  // rather than listing the same page under two group labels.
  if (
    sessionCan(session, "tenant:manage") &&
    !sessionCan(session, "platform:manage_tenants", { layer: "L1" })
  ) {
    items.push({ to: "/atlas", label: "Platform atlas", icon: AtlasIcon });
  }
  // Support tickets are per user now (the user menu's "My support tickets");
  // platform_admin reaches the all-tenant list in the PLATFORM group below.
  return items;
}

// Platform-admin-only entries. The Workspace "Attestation" entry fans
// out to /reattestation for platform_admin (the schedule view) — but
// S-092 also gives platform_admin a per-record browser at /attestation.
// We surface that separately here so platform_admin can reach both
// surfaces from the sidebar without overloading the Workspace entry.
// "Tenants" (S-091) is the cross-tenant provisioning + browse surface.
const PLATFORM: NavItem[] = [
  { to: "/admin/tenants", label: "Organizations", icon: UsersIcon },
  { to: "/attestation", label: "Approval records", icon: ShieldIcon },
  { to: "/template-management", label: "Template Management", icon: StackIcon },
  { to: "/certification-queue", label: "Certification queue", icon: ShieldIcon },
  { to: "/classification", label: "Classification", icon: TagIcon },
  { to: "/ingestion", label: "Ingestion status", icon: StackIcon },
  { to: "/queue", label: "Review queue", icon: ShieldIcon },
  { to: "/atlas", label: "Platform atlas", icon: AtlasIcon },
  // [S-788a / E-HARNESS] The eval scorecard — read it before testing by hand.
  { to: "/platform/evals", label: "Evals", icon: PulseIcon },
  // Support portal — every tenant's problem reports.
  { to: "/support/tickets", label: "Support tickets", icon: PulseIcon },
  // [S-710 / E-PERSONA-ADMIN] agent:manage is platform_admin-only (same as
  // platform:manage_tenants, which gates `showPlatform` below), so this
  // entry is safe unconditionally in this group, same as its siblings.
  { to: "/admin/personas", label: "Persona configuration", icon: PersonaConfigIcon },
];

export function Sidebar(): JSX.Element {
  const session = useSession();
  const workspace = workspaceFor(session);
  // The Organization group renders whenever the caller holds at least one of
  // its (capability-gated) entries — so a persona with a single admin
  // capability sees just that surface, not the whole group or nothing.
  const orgItems = tenantAdminFor(session);
  const showPlatform = sessionCan(session, "platform:manage_tenants", {
    layer: "L1",
  });
  return (
    <aside className="sb" id="app-sidebar">
      <Link to="/home" className="sb__brand">
        <span className="sb__brand-mark">CS</span>
        <span className="sb__brand-text">
          ContractStudio<em>.ai</em>
        </span>
      </Link>

      <ChatNav
        tenantId={
          session?.activeTenantId ??
          (session?.studioId != null ? `tenant-${session.studioId}` : undefined)
        }
      />
      <NavGroup label="Workspace" items={workspace} />
      <NavGroup label="Operations" items={OPERATIONS} />
      {orgItems.length > 0 && (
        <NavGroup label="Organization" items={orgItems} />
      )}
      {showPlatform && <NavGroup label="Platform" items={PLATFORM} />}

      <UserMenu />
    </aside>
  );
}

function NavGroup({ label, items }: { label: string; items: NavItem[] }): JSX.Element {
  return (
    <div className="sb__group">
      <div className="sb__group-label">{label}</div>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            isActive ? "sb__item is-active" : "sb__item"
          }
        >
          <item.icon />
          <span className="sb__label">{item.label}</span>
          {item.count !== undefined && (
            <span className="count">{formatCount(item.count)}</span>
          )}
        </NavLink>
      ))}
    </div>
  );
}

function formatCount(n: number): string {
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return n.toString();
}

// [Chat Wireframe] Persistent "Chat" section above Workspace: a + to start a
// fresh chat (routes to the chat-first Home) and the recent conversations, each
// opening on Home via ?chat=<id>. The list scrolls once it exceeds ~5 rows.
// Chat is per-studio, so it only renders when a studio scope is resolved.
//
// [Studio Agents retirement] Every persona (drafting/negotiation/longform via
// route_to_agent, analysis inline on the assistant persona) is reachable from
// a single Home conversation now — the standalone per-persona tab picker is
// gone. This list is no longer filtered to agentKind:"assistant", so a
// conversation that routed through a specialist mid-chat still shows up here
// under the SAME entry (routing happens within one session, not a new one).
function ChatNav({
  tenantId,
}: {
  tenantId: string | undefined;
}): JSX.Element | null {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const activeChat = params.get("chat");
  // Collapsed by default; the choice persists per browser.
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem("sb.chatOpen") === "1";
    } catch {
      return false;
    }
  });
  const setOpenState = (next: boolean): void => {
    setOpen(next);
    try {
      localStorage.setItem("sb.chatOpen", next ? "1" : "0");
    } catch {
      /* ignore private-mode storage errors */
    }
  };
  const toggle = (): void => setOpenState(!open);
  // Search is a lightweight client-side title filter (no backend query) —
  // opening it also expands the section, since there's nothing to search
  // while collapsed.
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const toggleSearch = (): void => {
    setSearchOpen((s) => {
      const next = !s;
      if (next && !open) setOpenState(true);
      if (!next) setQuery("");
      return next;
    });
  };
  const sessions = useChatSessions({
    tenantId,
    enabled: tenantId !== undefined && open,
  });
  const del = useDeleteChatSession();
  const onDelete = (id: string): void => {
    if (del.isPending) return;
    del.mutate(
      { sessionId: id, ...(tenantId !== undefined ? { tenantId } : {}) },
      {
        // If the deleted chat is the one open on Home, leave it.
        onSuccess: () => {
          if (id === activeChat) navigate("/home");
        },
      }
    );
  };
  if (tenantId === undefined) return null;
  const items = sessions.data ?? [];
  const q = query.trim().toLowerCase();
  const filteredItems =
    q.length === 0 ? items : items.filter((s) => s.title.toLowerCase().includes(q));
  return (
    <div className={open ? "sb__group sb__chatnav is-open" : "sb__group sb__chatnav"}>
      <div className="sb__chatnav-head">
        <button
          type="button"
          className="sb__chatnav-toggle"
          onClick={toggle}
          aria-expanded={open}
        >
          <ChevronIcon />
          <span className="sb__group-label">Chat</span>
        </button>
        <button
          type="button"
          className={searchOpen ? "sb__chatnav-search is-active" : "sb__chatnav-search"}
          title="Search chat history"
          aria-label="Search chat history"
          aria-expanded={searchOpen}
          onClick={toggleSearch}
        >
          <SearchIcon />
        </button>
        <button
          type="button"
          className="sb__chatnav-new"
          // [S-512] Start a fresh chat. Clears ?chat and lands on Home's
          // new-chat state, where the Contract Assistant greets you (so it
          // always reads as a new chat, never a dead no-op). No empty session
          // is created — the real session is minted on first send.
          title="New chat"
          aria-label="New chat"
          onClick={() => navigate("/home")}
        >
          <PlusIcon />
        </button>
      </div>
      {open && searchOpen && (
        <div className="sb__chatnav-searchbox">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chats by title…"
            aria-label="Search chats by title"
            autoFocus
          />
        </div>
      )}
      {open &&
        (sessions.isLoading ? (
          <div className="sb__chatnav-empty">Loading…</div>
        ) : filteredItems.length === 0 ? (
          <div className="sb__chatnav-empty">
            {items.length === 0 ? "No chats yet" : "No chats match your search"}
          </div>
        ) : (
          <div className="sb__chatnav-list">
            {filteredItems.map((s) => (
              <div
                key={s.id}
                className={
                  s.id === activeChat
                    ? "sb__chatnav-item is-active"
                    : "sb__chatnav-item"
                }
              >
                <Link
                  to={`/home?chat=${encodeURIComponent(s.id)}`}
                  className="sb__chatnav-link"
                  title={s.title}
                >
                  {s.title}
                </Link>
                <button
                  type="button"
                  className="sb__chatnav-del"
                  title="Delete chat"
                  aria-label={`Delete chat: ${s.title}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete(s.id);
                  }}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
          </div>
        ))}
    </div>
  );
}

function PlusIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ChevronIcon(): JSX.Element {
  return (
    <svg className="sb__chatnav-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function TrashIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M6 6l1 14h10l1-14" />
    </svg>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Icons — inline SVGs, exact stroke vocabulary from the wireframe set
// (docs/Wireframes/cs-components.js NAV). Pure React; no icon library.
// ────────────────────────────────────────────────────────────────────────

function HomeIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12 12 4l9 8" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}

function CertifiedIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M21 14v6 H3 V3 h6" />
    </svg>
  );
}

function SearchIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function BriefIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 4h14v16 H5z" />
      <path d="M9 8h6 M9 12h6 M9 16h4" />
    </svg>
  );
}

function DraftIcon(): JSX.Element {
  // Document with a pencil — an editable, versioned draft marker. Distinct
  // from BriefIcon (read-only brief).
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3H6v18h12V9z" />
      <path d="M14 3v6h4" />
      <path d="M9 14h4" />
    </svg>
  );
}

// ArchiveIcon was removed with the Studio Archive nav entry — the archive is
// now the Upload Portal's "All uploads" tab. Restore from git history if a
// dedicated entry is ever reinstated.

function ClockIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
}

function PulseIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h4l2-3 3 6 2-3h6" />
    </svg>
  );
}

// AnalyticsIcon, IngestionIcon, and QueueIcon were removed alongside
// their sidebar entries — the screens are stubs and the nav entries
// led to "Not yet built." Restore from git history when the
// corresponding screens ship.

function UploadIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 16V6" />
      <path d="m7 11 5-5 5 5" />
      <path d="M5 20h14" />
    </svg>
  );
}

function ShieldIcon(): JSX.Element {
  // Attestation shield — matches the wireframe's "att" entry icon.
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" />
    </svg>
  );
}

function UsersIcon(): JSX.Element {
  // Tenant / user-management — Lucide "users" silhouette stroke vocabulary,
  // sized to read at the sidebar density.
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function StackIcon(): JSX.Element {
  // Three stacked sheets — a "template library / authoring" marker.
  // Distinct from CertifiedIcon (which carries the seal semantics on
  // the read-only /library entry in the Workspace group).
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 4h10v10H7z" />
      <path d="M5 7v10h10" />
      <path d="M3 10v10h10" />
    </svg>
  );
}

function MarketplaceIcon(): JSX.Element {
  // Shopping bag — browse/import/exclude marker, distinct from StackIcon's
  // stacked-sheets authoring semantics. Matches the wireframe's sidebar glyph.
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 8h16l-1 12H5z" />
      <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    </svg>
  );
}

function TagIcon(): JSX.Element {
  // Tag — classification taxonomy marker.
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  );
}

function AtlasIcon(): JSX.Element {
  // Compass / map — a meta-view marker. Pure stroke; works on the
  // dark sidebar.
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <polygon points="14.5 9.5 9.5 14.5 9.5 9.5 14.5 14.5" />
    </svg>
  );
}

// [S-710 / E-PERSONA-ADMIN] Sliders — a "tune the behavior" marker, distinct
// from CogIcon (tenant Settings) and AtlasIcon (the read-only surface map).
function PersonaConfigIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="4" y1="6" x2="20" y2="6" />
      <circle cx="9" cy="6" r="2" fill="currentColor" stroke="none" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <circle cx="15" cy="12" r="2" fill="currentColor" stroke="none" />
      <line x1="4" y1="18" x2="20" y2="18" />
      <circle cx="11" cy="18" r="2" fill="currentColor" stroke="none" />
    </svg>
  );
}

function CogIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}
